/**
 * Dashboard cache API.
 * All dashboard caching (timestamp + chart data) goes through this module.
 * Chart cache keys in Dashboard.js include source (`main` vs `opt-{planId}`) via getChartRequestKey.
 *
 * Current implementation: in-memory (Redux). To switch to Redis or another
 * backend later, replace the implementation here and expose the same API
 * (consider async get/set if using a remote cache).
 */

import {
  setDashboardCacheTimestamp,
  clearDashboardCache,
  setDashboardChartCache as setDashboardChartCacheAction,
} from "../slices/dashboardSlice";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Cache TTL for dashboard data (5 minutes). Defined here so Webpack can resolve the named export reliably. */
export const DASHBOARD_CACHE_TTL_MS = 5 * 60 * 1000;

// ---------------------------------------------------------------------------
// Serialization (Redux state must be serializable – no functions)
// ---------------------------------------------------------------------------

/**
 * Deep-clone and strip non-serializable values (functions) so payload is safe for Redux.
 * @param {unknown} value
 * @returns {unknown}
 */
function sanitizeForRedux(value) {
  if (value === null || typeof value !== "object") return value;
  if (typeof value === "function") return undefined;
  if (Array.isArray(value)) return value.map(sanitizeForRedux);
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (typeof v === "function") continue;
    const s = sanitizeForRedux(v);
    if (s !== undefined) out[k] = s;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Selectors (read from state – replace with Redis get when migrating)
// All cache is per logged-in user (userId) so data is not visible to other users.
// ---------------------------------------------------------------------------

/**
 * @param {object} state - Redux state
 * @param {string} userId - logged-in user id (e.g. emp_id)
 */
export const getCacheTimestamp = (state, userId) => {
  if (userId === undefined || userId === null) return null;
  return state.dashboard?.cacheByUser?.[userId]?.dashboardCacheTimestamp ?? null;
};

/**
 * @param {object} state - Redux state
 * @param {string} userId - logged-in user id
 */
export const getCachedChartData = (state, userId) => {
  if (userId === undefined || userId === null) return null;
  return state.dashboard?.cacheByUser?.[userId]?.cachedChartData ?? null;
};

/**
 * @param {object} state - Redux state
 * @param {string} userId - logged-in user id
 * @param {boolean} forceRefresh - whether user requested a refresh
 * @returns {boolean} true only when timestamp is valid and cached chart data exists and is non-empty
 */
export const isCacheValid = (state, userId, forceRefresh = false) => {
  const ts = getCacheTimestamp(state, userId);
  if (ts == null || forceRefresh) return false;
  if (Date.now() - ts >= DASHBOARD_CACHE_TTL_MS) return false;
  const cached = getCachedChartData(state, userId);
  if (cached == null || typeof cached !== "object" || Object.keys(cached).length === 0) return false;
  return true;
};

// ---------------------------------------------------------------------------
// Actions (write – replace with Redis set when migrating)
// All take userId so cache is stored per user.
// ---------------------------------------------------------------------------

/** @param {string} userId - logged-in user id */
export const setCacheTimestamp = (timestamp, userId) =>
  setDashboardCacheTimestamp({ userId, timestamp });

/** @param {string} userId - logged-in user id */
export const clearCache = (userId) => clearDashboardCache(userId);

/**
 * Chart cache payload is sanitized (functions stripped). Use budgetBifurcationByDepartment to merge per-department budget data.
 * @param {object} payload - chart data (may include budgetBifurcationByDepartment: { [departmentId]: { budgetWiseBifurcationData } })
 * @param {string} userId - logged-in user id
 */
export const setChartCache = (payload, userId) =>
  setDashboardChartCacheAction({ userId, payload: sanitizeForRedux(payload) });
